import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import Select from 'app/frontend/base/components/Select'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamCalendarEvents } from 'app/frontend/base/config/queries'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleSlash2Icon,
  LockIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

dayjs.extend(isoWeek)

const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
  dayjs().startOf('isoWeek').add(index, 'day').format('dd'),
)
const isCalendarMonthValue = (value: string): value is string =>
  /^\d{4}-(0[1-9]|1[0-2])$/.test(value)

const EventCalendar = () => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const { value: monthValue, setValue: setMonthValue } =
    usePersistedHashValue<string>({
      defaultValue: dayjs().format('YYYY-MM'),
      isValid: isCalendarMonthValue,
      hashKey: 'month',
      persistInLocalStorage: false,
    })
  const month = useMemo(
    () => dayjs(`${monthValue}-01`).startOf('month'),
    [monthValue],
  )
  const monthKey = month.format('YYYY-MM')
  const {
    data: events = [],
    isLoading,
    isFetching,
  } = useTeamCalendarEvents(team.id, monthKey)

  const firstVisibleDate = useMemo(
    () => month.startOf('month').startOf('isoWeek'),
    [month],
  )
  const dayCount = useMemo(() => {
    const lastVisibleDate = month.endOf('month').endOf('isoWeek')
    return lastVisibleDate.diff(firstVisibleDate, 'day') + 1
  }, [firstVisibleDate, month])
  const days = useMemo(
    () =>
      Array.from({ length: dayCount }, (_, index) =>
        firstVisibleDate.add(index, 'day'),
      ),
    [dayCount, firstVisibleDate],
  )
  const lastCurrentMonthDayIndex = useMemo(
    () =>
      days.reduce(
        (lastIndex, date, index) =>
          date.month() === month.month() ? index : lastIndex,
        -1,
      ),
    [days, month],
  )
  const monthOptions = useMemo(() => {
    const currentMonth = dayjs().startOf('month')
    const startMonth = currentMonth.add(3, 'month')
    return Array.from({ length: 65 }, (_, index) => {
      const candidate = startMonth.subtract(index, 'month')
      const isCurrentMonthOption = candidate.isSame(currentMonth, 'month')
      return {
        value: candidate.format('YYYY-MM'),
        label: isCurrentMonthOption
          ? 'This month'
          : candidate.format('MMM YYYY'),
        sortLabel: candidate.format('YYYYMM'),
      }
    })
  }, [])

  const eventsByDate = useMemo(() => {
    const groupedEvents = new Map<string, wowaudit.EventReference[]>()
    events.forEach((event) => {
      const key = dayjs(event.date).format('YYYY-MM-DD')
      const list = groupedEvents.get(key) ?? []
      list.push(event)
      groupedEvents.set(key, list)
    })
    groupedEvents.forEach((list) => {
      list.sort((left, right) => {
        if (left.startTime === right.startTime) return left.id - right.id
        return left.startTime.localeCompare(right.startTime)
      })
    })
    return groupedEvents
  }, [events])

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center justify-between'>
        <Button
          type='button'
          variant='outline'
          size='xs'
          onClick={() =>
            setMonthValue(month.subtract(1, 'month').format('YYYY-MM'))
          }
          disabled={isFetching}
          className='h-7'
        >
          <ChevronLeftIcon className='h-4 w-4' />
        </Button>

        <Select
          className='h-7 w-36'
          value={monthValue}
          options={monthOptions}
          sortDirection='desc'
          setValue={(value) => {
            if (value && isCalendarMonthValue(value)) {
              setMonthValue(value)
            }
          }}
          disabled={isFetching}
        />

        <Button
          type='button'
          variant='outline'
          size='xs'
          onClick={() => setMonthValue(month.add(1, 'month').format('YYYY-MM'))}
          disabled={isFetching}
          className='h-7'
        >
          <ChevronRightIcon className='h-4 w-4' />
        </Button>
      </div>

      <Card className='relative overflow-hidden p-0'>
        {(isLoading || isFetching) && (
          <div className='absolute inset-0 z-20 flex items-center justify-center bg-card/60' />
        )}
        <div className='hidden border-b sm:grid sm:grid-cols-7'>
          {weekdayLabels.map((label) => (
            <div
              key={label}
              className='border-r border-border py-2 text-center last:border-r-0'
            >
              {label}
            </div>
          ))}
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-7 sm:auto-rows-fr'>
          {days.map((date, index) => {
            const key = date.format('YYYY-MM-DD')
            const dayEvents = eventsByDate.get(key) ?? []
            const hasMultipleEvents = dayEvents.length > 1
            const firstEvent = dayEvents[0]
            const dayTitle = firstEvent
              ? hasMultipleEvents
                ? `${dayEvents.length} events`
                : firstEvent.title ||
                  t(
                    `dashboard.team.events.activity-types.${firstEvent.activityType}`,
                  )
              : null
            const isLastColumn = (index + 1) % 7 === 0
            const isLastCalendarRow = index >= days.length - 7
            const isLastVisibleMobileItem = index === lastCurrentMonthDayIndex
            const isCurrentMonth = date.month() === month.month()
            const isToday = date.isSame(dayjs(), 'day')

            return (
              <div
                key={key}
                className={cn(
                  'h-28 border-b border-border p-1 flex-col gap-1 sm:border-r sm:border-b',
                  isCurrentMonth ? 'flex' : 'hidden sm:flex',
                  isLastColumn && 'sm:border-r-0',
                  isLastCalendarRow && 'sm:border-b-0',
                  isLastVisibleMobileItem && 'border-b-0',
                  isToday ? 'bg-primary/10' : '',
                  !isCurrentMonth && 'bg-card-nested/50',
                )}
              >
                <div className='px-1 min-w-0 flex items-center justify-between gap-2'>
                  <span
                    className={cn(
                      'tabular-nums',
                      isCurrentMonth
                        ? 'text-foreground'
                        : 'text-muted-foreground/50',
                      isToday && 'text-primary',
                    )}
                  >
                    {date.format('DD')}
                  </span>
                  {dayTitle && (
                    <span
                      className={cn(
                        'truncate text-xs font-medium',
                        dayEvents.length === 1 &&
                          dayEvents[0].status === 'cancelled'
                          ? 'text-destructive-foreground line-through'
                          : '',
                      )}
                    >
                      {dayTitle}
                    </span>
                  )}
                </div>

                <div className='flex-1 min-h-0 overflow-y-auto flex flex-col gap-1 px-1 pb-1'>
                  {dayEvents.map((event) => {
                    const presentCount = event.participantReferences.filter(
                      (participant) =>
                        presentParticipantStatuses.includes(participant.status),
                    ).length
                    const totalCount = event.participantReferences.length
                    const isCancelled = event.status === 'cancelled'
                    return (
                      <Link
                        key={event.id}
                        to={`${team.path}/events/${event.id}`}
                        className='relative min-h-[1.5rem] flex-1 rounded-md overflow-hidden border border-border group'
                      >
                        <EventAvatar
                          eventReference={event}
                          variant='square'
                          className={cn(
                            'absolute inset-0 h-full w-full rounded-none transition-transform duration-200 group-hover:scale-110',
                            isCancelled && 'grayscale opacity-80',
                          )}
                        />

                        <div
                          className={cn(
                            'relative z-10 flex h-full flex-col justify-between px-1 py-0.5 leading-tight text-card-foreground',
                            'filter-[drop-shadow(0_0_14px_rgba(0,0,0,1))_drop-shadow(0_0_6px_rgba(0,0,0,1))_drop-shadow(0_2px_3px_rgba(0,0,0,1))]',
                            isCancelled && 'text-muted-foreground',
                          )}
                        >
                          <div className='flex items-center justify-between gap-1 font-medium'>
                            <span className='truncate'>{event.startTime}</span>
                            <span className='truncate flex items-center gap-1'>
                              {hasMultipleEvents &&
                                event.status === 'locked' && (
                                  <LockIcon className='h-3.5 w-3.5 shrink-0 text-warning-foreground' />
                                )}
                              {hasMultipleEvents && isCancelled && (
                                <CircleSlash2Icon className='h-3.5 w-3.5 shrink-0 text-destructive-foreground' />
                              )}
                              {hasMultipleEvents
                                ? presentCount
                                : event.finishTime}
                            </span>
                          </div>
                          {!hasMultipleEvents && (
                            <div className='text-right text-base truncate flex items-center justify-end gap-1'>
                              {event.status === 'locked' && (
                                <LockIcon className='h-3.5 w-3.5 shrink-0 text-warning-foreground' />
                              )}
                              {isCancelled && (
                                <CircleSlash2Icon className='h-3.5 w-3.5 shrink-0 text-destructive-foreground' />
                              )}
                              <span>
                                {presentCount} / {totalCount}
                              </span>
                            </div>
                          )}
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

export default EventCalendar
